/**
 * Lesen und Setzen eines Wertes über einen Pfad — pur, ohne Abhängigkeiten.
 *
 * **Der Grund steht in der Falle von `PUT /api/v1/admin/rule-config`: die Route ersetzt
 * vollständig.** Weggelassene Felder fallen auf ihren Default zurück (`.prefault()` in
 * `packages/contracts/src/rule-config.ts`). Das Formular muss also immer die **ganze**
 * Konfiguration senden — es hält den geladenen Zustand und ersetzt darin ein einzelnes Blatt.
 * Sonst setzt ein Tester, der nur die Wächter-Reichweite ändert, nebenbei das Punkteziel und alle
 * Figurenzahlen zurück, und merkt es erst an den Spielergebnissen.
 *
 * **Neue Objekte statt Veränderung.** Dieselbe Entscheidung wie in der Engine: eine Funktion gibt
 * neuen Zustand zurück und verändert nichts. Für React ist das zusätzlich die Voraussetzung dafür,
 * dass unberührte Teilbäume identisch bleiben und nicht neu gezeichnet werden.
 *
 * **Ein unmöglicher Pfad wirft.** Er wäre ein Tippfehler in der Feldbeschreibung (`fields.ts`),
 * und still `undefined` zu liefern zeigte ein leeres Eingabefeld — das beim Speichern einen
 * Default überschreibt. Ein lauter Fehler erscheint dagegen sofort im Test.
 */

/** Ein Objekt, dessen Felder über einen Pfad erreichbar sind. */
type Record_ = { readonly [key: string]: unknown };

function isRecord(value: unknown): value is Record_ {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function segmentsOf(path: string): readonly string[] {
  const segments = path.split('.');
  if (path === '' || segments.some((segment) => segment === '')) {
    throw new Error(`Kein Pfad: "${path}"`);
  }
  return segments;
}

/** Der Wert an diesem Pfad. `undefined`, wenn der letzte Schlüssel fehlt. */
export function valueAt(source: unknown, path: string): unknown {
  const segments = segmentsOf(path);
  let current: unknown = source;

  for (const [index, segment] of segments.entries()) {
    if (!isRecord(current)) {
      throw new Error(`Der Pfad "${path}" führt durch keinen Wert (bei "${segment}")`);
    }
    current = current[segment];
    // Ein fehlender Schlüssel am **Ende** ist in Ordnung (`canJump` ist optional); ein fehlender
    // in der Mitte wäre ein falscher Pfad und fällt im nächsten Durchlauf auf.
    if (current === undefined && index < segments.length - 1) {
      throw new Error(`Der Pfad "${path}" führt durch keinen Wert (bei "${segment}")`);
    }
  }
  return current;
}

/**
 * Dieselbe Struktur mit einem ersetzten Wert.
 *
 * Alles außerhalb des Pfades bleibt **identisch** (nicht nur gleich): es wird weitergegeben und
 * nicht kopiert.
 */
export function withValueAt<T>(source: T, path: string, value: unknown): T {
  const segments = segmentsOf(path);

  const replace = (current: unknown, depth: number): unknown => {
    const segment = segments[depth];
    if (segment === undefined) {
      return value;
    }
    if (!isRecord(current)) {
      throw new Error(`Der Pfad "${path}" führt durch keinen Wert (bei "${segment}")`);
    }
    return { ...current, [segment]: replace(current[segment], depth + 1) };
  };

  return replace(source, 0) as T;
}
